/**
 * useSelectFileHandler
 *
 * Handles composer attachment selection and upload preparation.
 *
 * Primary responsibilities:
 * - Validate attachment limits before upload work starts
 * - Prepare image, file, audio, and document attachments
 * - Expose upload phase status for stable composer feedback
 *
 * Dependencies:
 * - Chat attachment context
 * - File creation and image processing helpers
 */

import { useContext, useEffect, useRef, useState } from 'react'
import { ChatbotUIChatContext } from '@/context/chat'
import { ChatbotUIContext } from '@/context/context'
import { createFile, waitForFileProcessing } from '@/db/files'
import { toast } from 'sonner'

import { guessFileExtensionByContentType } from '@/lib/content-type'
import { extractDocxRawText } from '@/lib/docx/extract-docx-raw-text'
import { processImage, processImageWithPreview, compressFileForUpload } from '@/lib/imageProcessing'
import { normalizeImageFile } from '@/lib/utils/imageNormalization'
import { LLM_LIST } from '@/lib/models/llm/llm-list'
import { getAttachmentUploadLimit } from '@/lib/subscription'
import { validateChatDraftAttachmentUpload } from '@/lib/chat/attachment-upload-limits'
import {
  createTryTempFileId,
  extractTryTempFileText,
  getTryAttachmentOverLimitMessage,
  isOverTryAttachmentLimit,
  isTryPdfFile,
  isTryUnsupportedMediaFile,
  setTryTempFileContent,
  TRY_USER_MAX_FILES_PER_UPLOAD
} from '@/lib/chat/try-temp-attachments'
import { useDelayedPaywall } from '@/hooks/use-delayed-paywall'
import {
  getAnalyticsPlanId,
  ProductAnalyticsEventNames,
  trackProductEvent
} from '@/lib/analytics/product-events'
import { clientMarkFeatureUsed } from '@/lib/discoverability/client'
import { v4 as uuidv4 } from 'uuid'
import type { ChatSettings } from '@/types/chat'
import { useScopedAttachmentKey } from './use-scoped-user-input'
// Removed @vercel/blob/client integration; we rely on Supabase storage instead.

export const ACCEPTED_FILE_TYPES = [
  'text/csv',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/json',
  'text/markdown',
  'application/pdf',
  'text/plain',
  // document extensions (browsers often give empty or octet-stream MIME)
  // Legacy .doc/.ppt are not processed server-side — omit from the picker.
  '.pdf',
  '.docx',
  '.csv',
  '.pptx',
  // programming files
  '.js',
  '.py',
  '.go',
  '.scala',
  '.c',
  '.cpp',
  '.cs',
  '.html',
  '.css',
  '.php',
  '.ts',
  '.java',
  '.rb',
  '.lua',
  '.sh',
  '.sql',
  '.yaml',
  '.yml',
  '.md',
  '.json',
  '.txt',
  // image files
  'image/*',
  'image/heic',
  'image/heif',
  // audio files
  'audio/*',
  'audio/mpeg', // .mp3
  'audio/mp3',
  'audio/mp4', // .m4a on many browsers
  'audio/x-m4a', // some Safari/iOS variants
  'audio/wav',
  'audio/x-wav',
  'audio/webm',
  'audio/ogg',
  'audio/aac',
  'audio/flac',
  'audio/aiff', // .aiff
  'audio/x-aiff', // .aiff variants
  // audio extensions (for browsers that provide empty/unknown type)
  '.mp3',
  '.wav',
  '.m4a',
  '.aac',
  '.flac',
  '.ogg',
  '.webm',
  '.aiff',
  '.aif',
  // video files
  'video/*',
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/ogg',
  'video/x-matroska',
  'video/x-msvideo',
  '.mp4',
  '.mov',
  '.webm',
  '.mkv',
  '.avi',
  // office files (OOXML only — legacy msword / ms-powerpoint unsupported)
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
  '.xls',
  '.xlsx',
  '.xlsm',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  // archives
  // "application/zip",
  // "application/x-zip-compressed",
  // "application/x-rar-compressed"
].join(',')

function classifyFileUploadFailureReason(message: string): string {
  const lower = message.toLowerCase()
  if (lower.includes('502') || lower.includes('bad gateway')) return 'upload_gateway'
  if (
    lower.includes('allows attachments up to') ||
    lower.includes('upgrade for larger')
  ) {
    return 'plan_limit'
  }
  if (lower.includes('unsupported file type')) return 'unsupported_type'
  if (
    lower.includes('failed to fetch') ||
    lower.includes('networkerror') ||
    lower.includes('network request failed') ||
    lower.includes('load failed') ||
    lower.includes('check your connection')
  ) {
    return 'network'
  }
  if (lower.includes('413') || lower.includes('too large')) return 'too_large'
  return 'other'
}

function getFileUploadErrorMessage(
  error: unknown,
  fallback = "Couldn't process that file. Try a different file."
) {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim()
  }
  if (typeof error === 'string' && error.trim()) {
    return error.trim()
  }
  return fallback
}

function isAcceptedComposerAttachment(file: File) {
  const mimeType = (file.type || '').toLowerCase().trim()
  const extension = `.${(file.name.split('.').pop() || '').toLowerCase()}`
  const accepted = ACCEPTED_FILE_TYPES.split(',')

  if (mimeType && accepted.includes(mimeType)) return true
  if (extension.length > 1 && accepted.includes(extension)) return true

  // Wildcard MIME entries (image/*, audio/*, video/*)
  if (
    mimeType.includes('/') &&
    accepted.some(
      (entry) =>
        entry.endsWith('/*') && mimeType.startsWith(`${entry.slice(0, -1)}`)
    )
  ) {
    return true
  }

  // Empty or generic MIME — trust the extension when it is in the accept list
  if (
    (!mimeType || mimeType === 'application/octet-stream') &&
    extension.length > 1 &&
    accepted.includes(extension)
  ) {
    return true
  }

  return false
}

interface UseSelectFileHandlerOptions {
  chatId?: string
  fileWorkspaceId?: string | null
  chatSettingsOverride?: ChatSettings | null
  uploadLimitChatId?: string | null
  uploadLimitSpaceId?: string | null
  /** Public /try composer — keep attachments local until login. */
  isPublic?: boolean
}

type ChatAttachmentUploadPhase =
  | 'preparing'
  | 'processing'
  | 'uploading'
  | 'indexing'
  | 'complete'

export type ChatAttachmentUploadStatus = {
  fileName: string
  fileSizeBytes: number
  label: string
  phase: ChatAttachmentUploadPhase
  progress: number
}

const COMPLETE_UPLOAD_STATUS_CLEAR_DELAY_MS = 650

const clampUploadProgress = (value: number) => Math.max(0, Math.min(100, value))

const getUploadStatus = (
  file: File,
  status: Omit<ChatAttachmentUploadStatus, 'fileName' | 'fileSizeBytes'>
): ChatAttachmentUploadStatus => ({
  fileName: file.name,
  fileSizeBytes: file.size,
  ...status,
  progress: clampUploadProgress(status.progress)
})

export const useSelectFileHandler = ({
  chatId,
  fileWorkspaceId = null,
  chatSettingsOverride = null,
  uploadLimitChatId = null,
  uploadLimitSpaceId = null,
  isPublic = false
}: UseSelectFileHandlerOptions = {}) => {
  const {
    selectedWorkspace,
    profile,
    activeAttachmentKey,
    getAttachmentStoreKey,
    getScopedAttachmentBindings,
    setIsPaywallOpen,
    setFiles,
    setUseRetrieval
  } = useContext(ChatbotUIContext)

  const { chatSettings } = useContext(ChatbotUIChatContext)
  const effectiveChatSettings = chatSettingsOverride ?? chatSettings

  const [filesToAccept, setFilesToAccept] = useState(ACCEPTED_FILE_TYPES)
  const [isUploading, setIsUploading] = useState(false)
  const [processingStatus, setProcessingStatus] = useState('')
  const [uploadStatus, setUploadStatus] = useState<ChatAttachmentUploadStatus | null>(null)
  const clearUploadStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scopedAttachmentKey = useScopedAttachmentKey({
    activeAttachmentKey,
    chatId,
    getAttachmentStoreKey
  })
  const scheduleUpgradePaywall = useDelayedPaywall(
    setIsPaywallOpen ? () => setIsPaywallOpen(true) : undefined
  )
  const effectiveFileWorkspaceId = fileWorkspaceId || selectedWorkspace?.id || null
  const hasWorkspaceUploadLimitContext = Boolean(uploadLimitChatId || uploadLimitSpaceId)
  const attachmentUploadLimit = getAttachmentUploadLimit(profile)

  const trackAttachmentLimitHit = (files: File[]) => {
    void trackProductEvent(ProductAnalyticsEventNames.ATTACHMENT_LIMIT_HIT, {
      surface: uploadLimitSpaceId ? 'spaces' : 'chat',
      source: 'chat_file_picker',
      current_plan: getAnalyticsPlanId(profile),
      effective_plan: getAnalyticsPlanId(profile),
      workspace_plan: getAnalyticsPlanId(profile),
      workspace_id: effectiveFileWorkspaceId ?? undefined,
      trigger: 'limit_hit',
      file_count: files.length,
      file_size_bytes: files.reduce((total, file) => total + file.size, 0),
      max_files: attachmentUploadLimit.filesPerUpload ?? undefined,
      max_attachment_size_mb: attachmentUploadLimit.maxAttachmentSizeBytes
        ? Math.round(attachmentUploadLimit.maxAttachmentSizeBytes / 1024 / 1024)
        : undefined
    })
    void trackProductEvent(ProductAnalyticsEventNames.UPGRADE_PROMPT_VIEWED, {
      surface: uploadLimitSpaceId ? 'spaces' : 'chat',
      source: 'chat_file_picker',
      current_plan: getAnalyticsPlanId(profile),
      effective_plan: getAnalyticsPlanId(profile),
      workspace_plan: getAnalyticsPlanId(profile),
      workspace_id: effectiveFileWorkspaceId ?? undefined,
      object_type: 'file',
      limit_type: 'file',
      fallback_offered: 'other',
      trigger: 'limit_hit'
    })
  }

  useEffect(() => {
    handleFilesToAccept()
  }, [effectiveChatSettings?.model])

  useEffect(() => {
    return () => {
      if (clearUploadStatusTimerRef.current) {
        clearTimeout(clearUploadStatusTimerRef.current)
      }
    }
  }, [])

  const clearUploadStatusSoon = () => {
    if (clearUploadStatusTimerRef.current) {
      clearTimeout(clearUploadStatusTimerRef.current)
    }

    clearUploadStatusTimerRef.current = setTimeout(() => {
      setUploadStatus(null)
      clearUploadStatusTimerRef.current = null
    }, COMPLETE_UPLOAD_STATUS_CLEAR_DELAY_MS)
  }

  const updateUploadStatus = (
    file: File,
    status: Omit<ChatAttachmentUploadStatus, 'fileName' | 'fileSizeBytes'>
  ) => {
    if (clearUploadStatusTimerRef.current) {
      clearTimeout(clearUploadStatusTimerRef.current)
      clearUploadStatusTimerRef.current = null
    }

    setUploadStatus(getUploadStatus(file, status))
  }

  const handleFilesToAccept = () => {
    const model = effectiveChatSettings?.model
    const FULL_MODEL = LLM_LIST.find((llm) => llm.modelId === model || llm.hostedId === model)

    if (!FULL_MODEL) return

    // Support all file types including images
    setFilesToAccept(ACCEPTED_FILE_TYPES)
  }

  const handleSelectDeviceFile = async (file: File) => {
    const isTryTempUpload = isPublic || !profile
    if (!effectiveChatSettings) return
    if (!isTryTempUpload && (!profile || !effectiveFileWorkspaceId)) return

    if (isTryTempUpload && isOverTryAttachmentLimit(file.size)) {
      toast.error(getTryAttachmentOverLimitMessage(file.name))
      return
    }

    const attachmentKey = scopedAttachmentKey
    const attachmentBindings = getScopedAttachmentBindings(attachmentKey)

    if (isTryTempUpload) {
      const draftCount =
        attachmentBindings.getSnapshot().newMessageFiles.length +
        attachmentBindings.getSnapshot().newMessageImages.length
      if (draftCount >= TRY_USER_MAX_FILES_PER_UPLOAD) {
        toast.error(
          `Try chats allow up to ${TRY_USER_MAX_FILES_PER_UPLOAD} attachments. Remove one or log in for more.`
        )
        return
      }
    }

    const validation = isTryTempUpload
      ? null
      : hasWorkspaceUploadLimitContext
        ? null
        : validateChatDraftAttachmentUpload(profile, {
            ...attachmentBindings.getSnapshot(),
            extraAttachments: [file]
          })

    if (validation && !validation.ok) {
      trackAttachmentLimitHit([file])
      toast.error(validation.message)
      scheduleUpgradePaywall()
      return
    }

    let loadingFileId: string | null = null
    let tempImageId: string | null = null
    let tempImageUrl: string | null = null

    try {
      setIsUploading(true)
      updateUploadStatus(file, {
        phase: 'preparing',
        progress: 6,
        label: 'Starting upload'
      })
      attachmentBindings.setShowFilesDisplay(true)
      if (!isTryTempUpload) {
        setUseRetrieval(true)
      }

      let processedFile = file
      let simplifiedFileType = guessFileExtensionByContentType(file.type, file.name)
      const originalFileExtension = '.' + (file.name.split('.').pop() || '').toLowerCase()

      // Handle images (including HEIC)
      if (file.type.startsWith('image/') || /\.(heic|heif)$/i.test(file.name)) {
        // Validate image size (max 20MB authenticated; try users already capped at 1MB)
        if (!isTryTempUpload && file.size > 20 * 1024 * 1024) {
          toast.error('Image is too large. Maximum size is 20MB.')
          setUploadStatus(null)
          return
        }

        // Validate image MIME type
        const allowedImageTypes = new Set([
          'image/jpeg',
          'image/png',
          'image/gif',
          'image/webp',
          'image/heic',
          'image/heif',
          'image/svg+xml'
        ])
        if (
          file.type &&
          !allowedImageTypes.has(file.type) &&
          !/\.(heic|heif)$/i.test(file.name)
        ) {
          toast.error('Unsupported image format.')
          setUploadStatus(null)
          return
        }

        tempImageId = `temp-image-${uuidv4()}`
        tempImageUrl = URL.createObjectURL(file)
        updateUploadStatus(file, {
          phase: 'preparing',
          progress: 18,
          label: 'Preparing preview'
        })
        attachmentBindings.setNewMessageImages((prev) => [
          ...prev,
          {
            messageId: tempImageId,
            path: '',
            base64: tempImageUrl,
            url: tempImageUrl,
            file
          }
        ])

        try {
          // Normalize image orientation based on EXIF data
          file = await normalizeImageFile(file)

          // Pre-compress large phone photos (12-48MP, up to 32MB) before
          // upload to storage. Bedrock limit is 5MB base64 (~3.75MB raw).
          // This avoids server-side re-compression every time the image
          // is fetched back from storage for model calls.
          if (file.size > 3 * 1024 * 1024) {
            file = await compressFileForUpload(file)
          }

          const imgFileData = await processImageWithPreview(file, (status) => {
            setProcessingStatus(status)
            updateUploadStatus(file, {
              phase: 'processing',
              progress: 42,
              label: status
            })
          })
          const {
            file: imgFile,
            url,
            base64
          } = imgFileData as { file: File; url: string; base64: string }

          processedFile = imgFile
          simplifiedFileType = imgFile.type.includes('heic') ? 'heic' : 'jpeg'
          updateUploadStatus(processedFile, {
            phase: 'complete',
            progress: 100,
            label: 'Ready to send'
          })

          attachmentBindings.setNewMessageImages((prev) =>
            prev.map((image) =>
              image.messageId === tempImageId
                ? {
                    messageId: tempImageId!,
                    path: '',
                    base64,
                    url,
                    file: processedFile
                  }
                : image
            )
          )

          if (tempImageUrl) {
            URL.revokeObjectURL(tempImageUrl)
            tempImageUrl = null
          }
        } catch (error) {
          console.error('Error processing image:', error)
          toast.error(
            getFileUploadErrorMessage(
              error,
              "Couldn't process that image. Try a different file."
            )
          )
          if (tempImageUrl) {
            URL.revokeObjectURL(tempImageUrl)
            tempImageUrl = null
          }
          attachmentBindings.setNewMessageImages((prev) =>
            prev.filter((image) => image.messageId !== tempImageId)
          )
          throw error
        }
      } else if (isAcceptedComposerAttachment(file)) {
        if (file.type.includes('pdf') || file.name.toLowerCase().endsWith('.pdf')) {
          simplifiedFileType = 'pdf'
        } else if (
          simplifiedFileType.includes(
            'vnd.openxmlformats-officedocument.wordprocessingml.document'
          ) ||
            simplifiedFileType.includes('docx') ||
            file.name.toLowerCase().endsWith('.docx')
        ) {
          simplifiedFileType = 'docx'
        }

        if (isTryTempUpload) {
          if (isTryUnsupportedMediaFile(file)) {
            toast.error('Log in to attach audio or video.')
            setUploadStatus(null)
            return
          }
          if (isTryPdfFile(file, simplifiedFileType)) {
            toast.error('Log in to attach PDFs for full analysis.')
            setUploadStatus(null)
            return
          }
        }

        loadingFileId = isTryTempUpload ? createTryTempFileId() : `loading-${uuidv4()}`
        updateUploadStatus(file, {
          phase: 'preparing',
          progress: 16,
          label: 'Preparing file'
        })
        attachmentBindings.setNewMessageFiles((prev) => [
          ...prev,
          {
            id: loadingFileId,
            name: file.name,
            type: simplifiedFileType,
            file: file,
            isTemporary: isTryTempUpload
          }
        ])

        let extractedText: string | undefined

        if (simplifiedFileType === 'docx') {
          updateUploadStatus(file, {
            phase: 'processing',
            progress: 28,
            label: 'Reading document'
          })
          const arrayBuffer = await file.arrayBuffer()
          extractedText = await extractDocxRawText(arrayBuffer)
        }

        const isAudioFile =
          file.type.startsWith('audio/') ||
          ['.mp3', '.wav', '.m4a', '.aac', '.flac', '.ogg', '.webm', '.aiff', '.aif'].includes(
            '.' + file.name.split('.').pop()?.toLowerCase()
          )

        if (isTryTempUpload) {
          updateUploadStatus(file, {
            phase: 'processing',
            progress: 48,
            label: 'Reading file'
          })
          const tempText =
            extractedText ||
            (await extractTryTempFileText(processedFile, simplifiedFileType))

          if (!tempText?.trim()) {
            toast.error(
              "Couldn't read that file here. Try a text file, or sign in for full support."
            )
            attachmentBindings.setNewMessageFiles((prev) =>
              prev.filter((item) => item.id !== loadingFileId)
            )
            setUploadStatus(null)
            return
          }

          setTryTempFileContent(loadingFileId, tempText)
          updateUploadStatus(processedFile, {
            phase: 'complete',
            progress: 100,
            label: 'Ready to send'
          })
          attachmentBindings.setNewMessageFiles((prev) =>
            prev.map((item) =>
              item.id === loadingFileId
                ? {
                    id: loadingFileId!,
                    name: processedFile.name,
                    type: simplifiedFileType,
                    file: processedFile,
                    isTemporary: true,
                    tempContent: tempText
                  }
                : item
            )
          )
          void trackProductEvent(ProductAnalyticsEventNames.FILE_UPLOADED, {
            surface: 'public_chat',
            source: 'try_temp_file_picker',
            current_plan: 'free',
            file_count: 1,
            file_size_bytes: processedFile.size
          })
          void clientMarkFeatureUsed({
            featureKey: 'files',
            surface: 'files',
            trigger: 'file_uploaded'
          })
          return
        }

        const needsConversion =
          isAudioFile &&
          (file.type.includes('m4a') ||
            file.name.toLowerCase().endsWith('.m4a') ||
            file.type.includes('aiff') ||
            file.name.toLowerCase().endsWith('.aiff') ||
            file.name.toLowerCase().endsWith('.aif'))

        if (needsConversion) {
          try {
            setProcessingStatus('Converting audio format...')
            updateUploadStatus(file, {
              phase: 'processing',
              progress: 34,
              label: 'Converting audio format'
            })
            const { FFmpeg } = await import('@ffmpeg/ffmpeg')
            const { fetchFile, toBlobURL } = await import('@ffmpeg/util')

            const ffmpeg = new FFmpeg()

            const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd'
            await ffmpeg.load({
              coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
              wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm')
            })

            const fileExt = file.name.split('.').pop()?.toLowerCase() || 'audio'
            const inputFileName = `input.${fileExt}`
            await ffmpeg.writeFile(inputFileName, await fetchFile(file))

            await ffmpeg.exec(['-i', inputFileName, '-acodec', 'libmp3lame', 'output.mp3'])

            const data = await ffmpeg.readFile('output.mp3')
            const mp3File = new File([data], file.name.replace(/\.(m4a|aiff|aif)$/i, '.mp3'), {
              type: 'audio/mp3'
            })

            processedFile = mp3File
            simplifiedFileType = 'mp3'
            setProcessingStatus('Upload and transcribing...')
            updateUploadStatus(processedFile, {
              phase: 'uploading',
              progress: 58,
              label: 'Uploading audio'
            })
          } catch (conversionError) {
            console.error('Audio conversion failed:', conversionError)
            toast.error("Couldn't convert that audio. Try an MP3 or WAV file.")
            attachmentBindings.setNewMessageFiles((prev) =>
              prev.filter((item) => item.id !== loadingFileId)
            )
            setUploadStatus(null)
            return
          }
        }

        updateUploadStatus(processedFile, {
          phase: 'uploading',
          progress: 68,
          label: 'Uploading file'
        })
        const createdFile = await createFile(
          processedFile,
          {
            user_id: profile!.user_id,
            description: '',
            file_path: '',
            name: processedFile.name,
            size: processedFile.size,
            tokens: 0,
            type: simplifiedFileType
          },
          effectiveFileWorkspaceId!,
          effectiveChatSettings.embeddingsProvider,
          extractedText,
          {
            uploadLimitChatId,
            uploadLimitSpaceId
          }
        )

        setFiles((prev) => [...prev, createdFile])

        // Processing now runs on the worker (off the chat event loop), so the
        // file comes back 'pending'. Poll until it's indexed before marking it
        // ready — chat retrieval tolerates pending/processing files, but
        // gating "ready" here keeps the old UX (don't imply a file is usable
        // before its content is searchable). On timeout we let the user send
        // anyway so a slow worker never permanently blocks the composer.
        const alreadyProcessed =
          createdFile.processing_status === 'completed' ||
          createdFile.processing_status === 'failed'

        if (!alreadyProcessed) {
          updateUploadStatus(processedFile, {
            phase: 'indexing',
            progress: 85,
            label: 'Processing file'
          })

          const pollResult = await waitForFileProcessing(createdFile.id)

          if (pollResult === 'failed') {
            toast.error(
              `Couldn't process "${processedFile.name}". It was uploaded but may not be searchable.`
            )
          }
        }

        updateUploadStatus(processedFile, {
          phase: 'complete',
          progress: 100,
          label: 'Ready to send'
        })
        void trackProductEvent(ProductAnalyticsEventNames.FILE_UPLOADED, {
          surface: uploadLimitSpaceId ? 'spaces' : 'chat',
          source: 'chat_file_picker',
          current_plan: getAnalyticsPlanId(profile),
          file_count: 1,
          file_size_bytes: processedFile.size
        })
        void clientMarkFeatureUsed({
          featureKey: 'files',
          surface: uploadLimitSpaceId ? 'spaces' : 'files',
          trigger: 'file_uploaded'
        })
        attachmentBindings.setNewMessageFiles((prev) =>
          prev.map((item) =>
            item.id === loadingFileId
              ? {
                  id: createdFile.id,
                  name: createdFile.name,
                  type: createdFile.type,
                  file: processedFile
                }
              : item
          )
        )
      } else {
        const extensionHint = originalFileExtension.length > 1 ? originalFileExtension : 'unknown type'
        const unsupportedMessage = `Unsupported file type (${extensionHint}). Try PDF, DOCX, PPTX, TXT, or an image.`
        toast.error(unsupportedMessage)
        console.error('Unsupported file type', file.type || extensionHint, file.name)
        void trackProductEvent(ProductAnalyticsEventNames.FILE_UPLOAD_FAILED, {
          surface: uploadLimitSpaceId ? 'spaces' : 'chat',
          source: 'chat_file_picker',
          current_plan: getAnalyticsPlanId(profile),
          file_count: 1,
          file_size_bytes: file.size,
          reason: 'unsupported_type',
          file_extension:
            originalFileExtension.length > 1
              ? originalFileExtension.replace(/^\./, '')
              : undefined
        })
        setUploadStatus(null)
        return
      }
    } catch (error) {
      console.error('Error handling file:', error)
      const message = getFileUploadErrorMessage(error)
      const extension = (file.name.split('.').pop() || '').toLowerCase() || undefined

      void trackProductEvent(ProductAnalyticsEventNames.FILE_UPLOAD_FAILED, {
        surface: uploadLimitSpaceId ? 'spaces' : 'chat',
        source: 'chat_file_picker',
        current_plan: getAnalyticsPlanId(profile),
        file_count: 1,
        file_size_bytes: file.size,
        reason: classifyFileUploadFailureReason(message),
        file_extension: extension
      })

      toast.error(message, {
        duration: 10000,
        action: {
          label: 'Retry',
          onClick: () => {
            void handleSelectDeviceFile(file)
          }
        }
      })

      if (tempImageUrl) {
        URL.revokeObjectURL(tempImageUrl)
      }
      if (tempImageId) {
        attachmentBindings.setNewMessageImages((prev) =>
          prev.filter((img) => img.messageId !== tempImageId)
        )
      }
      if (loadingFileId) {
        attachmentBindings.setNewMessageFiles((prev) =>
          prev.filter((tempFile) => tempFile.id !== loadingFileId)
        )
      }
    } finally {
      setIsUploading(false)
      setProcessingStatus('')
      clearUploadStatusSoon()
    }
  }

  const handleSelectDeviceFiles = async (files: FileList | File[]) => {
    const nextFiles = Array.from(files)
    const isTryTempUpload = isPublic || !profile

    if (isTryTempUpload) {
      const attachmentBindings = getScopedAttachmentBindings(scopedAttachmentKey)
      const draftCount =
        attachmentBindings.getSnapshot().newMessageFiles.length +
        attachmentBindings.getSnapshot().newMessageImages.length
      const remainingSlots = Math.max(0, TRY_USER_MAX_FILES_PER_UPLOAD - draftCount)

      if (remainingSlots === 0) {
        toast.error(
          `Try chats allow up to ${TRY_USER_MAX_FILES_PER_UPLOAD} attachments. Remove one or log in for more.`
        )
        return
      }

      const allowed: File[] = []
      for (const file of nextFiles) {
        if (allowed.length >= remainingSlots) break
        if (isOverTryAttachmentLimit(file.size)) {
          toast.error(getTryAttachmentOverLimitMessage(file.name))
          continue
        }
        allowed.push(file)
      }

      for (const file of allowed) {
        await handleSelectDeviceFile(file)
      }
      return
    }

    const attachmentBindings = getScopedAttachmentBindings(scopedAttachmentKey)
    const validation = hasWorkspaceUploadLimitContext
      ? null
      : validateChatDraftAttachmentUpload(profile, {
          ...attachmentBindings.getSnapshot(),
          extraAttachments: nextFiles
        })

    if (validation && !validation.ok) {
      trackAttachmentLimitHit(nextFiles)
      toast.error(validation.message)
      scheduleUpgradePaywall()
      return
    }

    for (const file of nextFiles) {
      await handleSelectDeviceFile(file)
    }
  }

  return {
    handleSelectDeviceFile,
    handleSelectDeviceFiles,
    filesToAccept,
    isUploading,
    uploadStatus,
    processingStatus,
    attachmentUploadLimit
  }
}

// Function to convert file to Base64
const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () =>
      reject(new Error(reader.error?.message || 'Failed to read file as base64'))
  })
}

// Example handler function
const handleFileSelection = async (selectedFile: File) => {
  try {
    const base64 = await convertFileToBase64(selectedFile)
    const url = URL.createObjectURL(selectedFile)

    const fileData = {
      path: '',
      base64: base64, // Explicit initializer
      url: url,
      file: selectedFile
    }

    // Further processing with fileData
  } catch (error) {
    console.error('Error processing file:', error)
  }
}
